import { Item } from '#/client/models';
import { isAudio } from '#/hooks/playback/utils';
import { useConfig } from '#/hooks/useConfig';
import { useAuthStore } from '#/store/auth/auth.store';

/**
 * Selects the parental system to be used for a given item, based on the app-level configured system,
 * the user-level list of preferred systems, and item-level supported systems.
 * @param item The item for which to select the parental system.
 * @returns The selected parental system or undefined.
 */
export const useParentalSystemForItem = (item: Item | undefined) => {
  // app-level configured system as a fallback if user-level preference is not available, and the item supports it
  const { app } = useConfig();
  const appParentalSystem = app.parentalSystem;

  // user-level preferred systems, in order
  const { profile } = useAuthStore();
  const userPreferredParentalSystems =
    profile?.configuration?.parentalControl?.preferredParentalRatingSchemes ??
    [];

  // audio content don't have parental ratings
  const parentalRatings = isAudio(item) ? [] : item?.video?.parentalRatings;

  // item-level supported systems, order doesn't matter
  const itemSupportedParentalSystems =
    parentalRatings?.map((system) => system.type) ?? [];

  return (
    // any of the user's preferred systems (in order) are supported by the item?
    userPreferredParentalSystems.find((system) =>
      itemSupportedParentalSystems.includes(system)
    ) ??
    // is the app-level system supported by the item?
    (appParentalSystem &&
    itemSupportedParentalSystems.includes(appParentalSystem)
      ? appParentalSystem
      : undefined)
  );
};
